import { defaultChargebeeLocale, supportedChargebeeLocales } from 'config/Vars'

import { ChargebeeSubscriptionForm } from 'modules/billing/components/PaymentGateway/FullScreenStep/form'
import { SimpleChargebeeSubscription, getSimpleSubscriptionItems } from 'modules/billing/lib/prices'

export const clearCardNumber = (value: string): string => {
  value = value.replace(/[^\d]/g, '')
  value = value.substring(0, 16)
  value = value.replace(/\d{4}(?=.)/g, '$& ')
  return value
}

export const clearCardExpiry = (value: string): string => {
  value = value.replace(/[^\d]/g, '')
  value = value.substring(0, 4)
  if (value === '') return ''
  const firstNumber = parseInt(value.charAt(0))
  if (firstNumber > 1) {
    value = '0' + value.substring(0, 3)
  } else if (firstNumber === 1) {
    const secondNumber = parseInt(value.charAt(1))
    if (secondNumber > 2) {
      value = '0' + value.substring(0, 3)
    }
  }
  value = value.replace(/\d{2}(?=.)/g, '$& / ')
  return value
}

export const clearCVV = (value: string): string => {
  value = value.replace(/[^\d]/g, '')
  value = value.substring(0, 3)
  return value
}

const getNetsuiteLocationByCountry = (country: string) => {
  switch (country) {
    case 'br':
      return 'Brazil'
    case 'fr':
      return 'France'
    case 'de':
      return 'Germany'
    case 'it':
      return 'Italy'
    case 'pt':
      return 'Portugal'
    case 'es':
      return 'Spain'
    case 'gb':
      return 'UK'
    case 'us':
      return 'US'
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'cr':
    case 'cu':
    case 'do':
    case 'ec':
    case 'gt':
    case 'hn':
    case 'mx':
    case 'ni':
    case 'pa':
    case 'pe':
    case 'pr':
    case 'py':
    case 'sv':
    case 'uy':
    case 've':
      return 'LATAM'
    default:
      return 'ROW'
  }
}

const getHeadquarter = (country: string) => {
  return ['mx', 'br'].includes(country) ? country : 'es'
}

interface PaymentFields {
  card_number: string
  card_expiry_month: string
  card_expiry_year: string
  card_cvv: string
  iban: string
}

const getPaymentFields = (form: ChargebeeSubscriptionForm): PaymentFields => {
  const fields = {
    card_number: '',
    card_expiry_month: '',
    card_expiry_year: '',
    card_cvv: '',
    iban: '',
  }
  if (form.get('payment_method').value === 'card') {
    fields.card_number = form.get('card_number').value.replace(/[^\d]/g, '')
    fields.card_expiry_month = form.get('card_expiry').value?.split(' / ')[0]
    fields.card_expiry_year = form.get('card_expiry').value?.split(' / ')[1]
    fields.card_cvv = form.get('card_cvv').value
    return fields
  }
  if (form.get('payment_method').value === 'bank-transfer') {
    fields.iban = form.get('iban').value
    return fields
  }
  return fields
}

type ChargebeeSubscriptionData = {
  headquarter: string
  country: string
  company: string
  address_line: string
  email: string
  payment_method: string
  cf_netsuite_location: string
  mx_tax_system: string
  vat_number: string
  city: string
  zip: string
  state: string
  iban: string
  card_number: string
  card_expiry_month: string | undefined
  card_expiry_year: string | undefined
  card_cvv: string
  locale: string
  subscription_items: string
}

export const getChargebeeSubscriptionData = ({
  form,
  planConfig,
  locale,
}: {
  form: ChargebeeSubscriptionForm
  planConfig: SimpleChargebeeSubscription[]
  locale: string
}): ChargebeeSubscriptionData => {
  const planDataParams = planConfig.map((subscription: SimpleChargebeeSubscription) =>
    getSimpleSubscriptionItems(subscription)
  )
  const cleanLocale = supportedChargebeeLocales.includes(locale.split('-')[0] || '')
    ? locale.split('-')[0]
    : defaultChargebeeLocale

  const subscription_items = JSON.stringify(
    planDataParams.map((plan) => {
      const { itemId, ...rest } = plan
      return Object.assign({}, rest, { item_price_id: itemId })
    })
  )

  const vat_number = form
    .get('vat_number')
    .value.replace(/\s/g, '')
    .replace(new RegExp(`^${form.get('country').value}`, 'i'), '')

  const headquarter = getHeadquarter(form.get('country').value)

  return {
    headquarter: headquarter,
    country: form.get('country').value,
    company: sanitizeByHeadquarter(form.get('company').value, headquarter),
    address_line: sanitizeByHeadquarter(form.get('address_line').value, headquarter),
    email: form.get('email').value,
    payment_method: form.get('payment_method').value,
    cf_netsuite_location: getNetsuiteLocationByCountry(form.get('country').value),
    mx_tax_system: form.get('mx_tax_system').value,
    vat_number: vat_number,
    city: sanitizeByHeadquarter(form.get('city').value, headquarter),
    zip: form.get('zip').value,
    state: sanitizeByHeadquarter(form.get('state').value, headquarter),
    locale: cleanLocale || defaultChargebeeLocale,
    subscription_items,
    ...getPaymentFields(form),
  }
}

const sanitizeByHeadquarter = (value: string, headquarter: string): string => {
  if (headquarter === 'mx') {
    const sanitizedValue = value
      .toUpperCase()
      .replace(/[^\x00-\x7F]/g, (match) => {
        return match.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      })
      .replace(/[^A-Z0-9\s]/g, '')
    return sanitizedValue
  }
  return value
}
